import {
  Badge,
  Button,
  Card,
  Divider,
  Group,
  NumberFormatter,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { IconCheck, IconSparkles } from "@tabler/icons-react";
import { checkout } from "../api";
import { usePlan } from "../hooks/usePlan";

function Feature({ children }) {
  return (
    <Group gap="xs" wrap="nowrap" align="flex-start">
      <IconCheck size={16} color="var(--mantine-color-green-6)" style={{ flexShrink: 0, marginTop: 3 }} />
      <Text size="sm" c="dimmed">{children}</Text>
    </Group>
  );
}

function PlanCard({ plan, currentPlan, highlight }) {
  const { t } = useTranslation("common");
  const [loading, setLoading] = useState(false);
  const isCurrent = currentPlan?.toLowerCase() === plan.id.toLowerCase();

  return (
    <Card
      withBorder
      shadow={highlight ? "md" : "sm"}
      p="xl"
      style={{
        display: "flex",
        flexDirection: "column",
        borderColor: highlight ? "var(--mantine-color-violet-6)" : undefined,
        borderWidth: highlight ? 2 : undefined,
      }}
      pos="relative"
    >
      {highlight && (
        <Badge
          color="violet"
          variant="filled"
          size="sm"
          pos="absolute"
          top={-10}
          left="50%"
          style={{ transform: "translateX(-50%)" }}
          leftSection={<IconSparkles size={12} />}
        >
          {t("pricing.plans.mostPopular")}
        </Badge>
      )}

      <Text fw={700} size="xl" mb={4}>{plan.name}</Text>

      <Group gap={4} align="baseline" mb={4}>
        {typeof plan.price === "number" ? (
          <>
            <Title order={2} style={{ fontSize: 42 }}>
              <NumberFormatter prefix="$" value={plan.price} thousandSeparator />
            </Title>
            <Text c="dimmed" size="sm">{t("pricing.plans.perMonth")}</Text>
          </>
        ) : (
          <Title order={2} style={{ fontSize: 42 }}>{plan.price}</Title>
        )}
      </Group>

      <Text size="sm" c="dimmed" mb="md">{plan.description}</Text>

      <Divider mb="md" />

      <Stack gap="xs" mb="xl" style={{ flex: 1 }}>
        {plan.features.map((f, i) => (
          <Feature key={i}>{f}</Feature>
        ))}
      </Stack>

      {isCurrent ? (
        <Button variant="light" color="green" fullWidth disabled>
          {t("pricing.plans.currentPlan")}
        </Button>
      ) : plan.cta === "contact" ? (
        <Button
          component="a"
          href="mailto:sales@autarky.dev"
          variant="outline"
          fullWidth
        >
          {t("pricing.contactSales")}
        </Button>
      ) : plan.price === 0 ? (
        <Button variant="light" fullWidth disabled>
          {t("pricing.plans.free")}
        </Button>
      ) : (
        <Button
          fullWidth
          loading={loading}
          variant={highlight ? "filled" : "outline"}
          onClick={() => {
            setLoading(true);
            checkout({ planName: plan.name, interval: "monthly" }, () =>
              setLoading(false),
            );
          }}
        >
          {isCurrent ? t("pricing.plans.currentPlan") : t("pricing.plans.subscribe")}
        </Button>
      )}
    </Card>
  );
}

function getPlans(t) {
  return [
    {
      id: "Free",
      name: "Free",
      price: 0,
      description: t("pricing.plans.freeDescription"),
      features: [
        t("pricing.features.products1k"),
        t("pricing.features.users", { count: 3 }),
        t("pricing.features.mapSharedSchemas"),
        t("pricing.features.qualityCheck"),
        t("pricing.features.unlimitedSubmissions"),
        t("pricing.features.paygCredits"),
      ],
    },
    {
      id: "Mini",
      name: "Mini",
      price: 49,
      description: t("pricing.plans.miniDescription"),
      features: [
        t("pricing.features.products1k"),
        t("pricing.features.users", { count: 3 }),
        t("pricing.features.ownSchemas"),
        t("pricing.features.smartImport"),
        t("pricing.features.readOnlyApi"),
        t("pricing.features.bulkExport"),
        t("pricing.features.credits350"),
      ],
    },
    {
      id: "Starter",
      name: "Starter",
      price: 500,
      description: t("pricing.plans.starterDescription"),
      features: [
        t("pricing.features.products10k"),
        t("pricing.features.users", { count: 3 }),
        t("pricing.features.ownSchemas"),
        t("pricing.features.smartImport"),
        t("pricing.features.readOnlyApi"),
        t("pricing.features.connectedSuppliers", { count: 50 }),
        t("pricing.features.credits10k"),
      ],
    },
    {
      id: "Professional",
      name: "Professional",
      price: 1500,
      description: t("pricing.plans.professionalDescription"),
      highlight: true,
      features: [
        t("pricing.features.products100k"),
        t("pricing.features.users", { count: 10 }),
        t("pricing.features.fullApi"),
        t("pricing.features.advancedPipelines"),
        t("pricing.features.connectedSuppliers", { count: 250 }),
        t("pricing.features.credits50k"),
      ],
    },
    {
      id: "Enterprise",
      name: "Enterprise",
      price: t("pricing.plans.customPrice"),
      description: t("pricing.plans.enterpriseDescription"),
      cta: "contact",
      features: [
        t("pricing.features.productsUnlimited"),
        t("pricing.features.usersUnlimited"),
        t("pricing.features.connectedSuppliersUnlimited"),
        t("pricing.features.dedicatedInfrastructure"),
        t("pricing.features.customSla"),
        t("pricing.features.volumeCreditPricing"),
      ],
    },
  ];
}

export function PricingBoxes() {
  const { t } = useTranslation("common");
  const { plan: currentPlan } = usePlan();
  const plans = getPlans(t);

  return (
    <SimpleGrid cols={{ base: 1, sm: 2, lg: 3, xl: 5 }} spacing="lg" my="lg">
      {plans.map((plan) => (
        <PlanCard
          key={plan.id}
          plan={plan}
          currentPlan={currentPlan}
          highlight={plan.highlight}
        />
      ))}
    </SimpleGrid>
  );
}
